<template>
  <div class="sns-login">
    <div class="sns-login__card">
      <!-- ===== LEFT: maritime hero (offline-safe SVG scene) ===== -->
      <div class="sns-login__hero">
        <!-- faint compass rose, drifting -->
        <svg class="sns-login__compass" viewBox="0 0 200 200" aria-hidden="true">
          <circle cx="100" cy="100" r="94" fill="none" stroke="currentColor" stroke-width="1" opacity="0.5" />
          <circle cx="100" cy="100" r="70" fill="none" stroke="currentColor" stroke-width="1" opacity="0.35" />
          <polygon points="100,8 112,100 100,120 88,100" fill="currentColor" opacity="0.9" />
          <polygon points="100,192 88,100 100,80 112,100" fill="currentColor" opacity="0.5" />
          <polygon points="8,100 100,88 120,100 100,112" fill="currentColor" opacity="0.6" />
          <polygon points="192,100 100,112 80,100 100,88" fill="currentColor" opacity="0.6" />
          <polygon points="40,40 100,92 96,96 92,100" fill="currentColor" opacity="0.3" />
          <polygon points="160,160 100,108 104,104 108,100" fill="currentColor" opacity="0.3" />
        </svg>

        <div class="sns-login__hero-content">
          <div class="sns-login__logo-badge">
            <img :src="require('../../assets/sns.jpeg')" alt="One Maritime Academy" />
          </div>
          <h2 class="sns-login__brand">ONE MARITIME ACADEMY</h2>
          <p class="sns-login__tagline">One Stop Maritime Solutions</p>

          <ul class="sns-login__points">
            <li><v-icon small color="#E9C57F">mdi-wifi-off</v-icon><span>Works fully offline, at sea or ashore</span></li>
            <li><v-icon small color="#E9C57F">mdi-certificate-outline</v-icon><span>Course certificates the moment you pass</span></li>
            <li><v-icon small color="#E9C57F">mdi-sync</v-icon><span>Syncs safely when you're back online</span></li>
          </ul>
        </div>

        <!-- waterline waves -->
        <svg class="sns-login__waves" viewBox="0 0 1440 140" preserveAspectRatio="none" aria-hidden="true">
          <path d="M0,70 C240,110 480,30 720,60 C960,90 1200,40 1440,70 L1440,140 L0,140 Z" fill="rgba(255,255,255,0.06)" />
          <path d="M0,95 C260,60 520,120 780,90 C1040,60 1240,110 1440,88 L1440,140 L0,140 Z" fill="rgba(255,255,255,0.10)" />
        </svg>
      </div>

      <!-- ===== RIGHT: sign-in form (logic unchanged) ===== -->
      <div class="sns-login__form">
        <div class="sns-login__form-inner">
          <!-- small logo shown only on narrow screens where the hero is hidden -->
          <img class="sns-login__mini-logo" :src="require('../../assets/sns.jpeg')" alt="One Maritime Academy" />

          <h1 class="sns-login__title">Welcome back</h1>
          <p class="sns-login__subtitle">Sign in to continue to your training.</p>

          <v-text-field
            v-model="form.email"
            outlined
            label="Email"
            prepend-inner-icon="mdi-email"
            :error-messages="errors.email"
          ></v-text-field>

          <v-text-field
            @keypress.enter="login"
            v-model="form.password"
            type="password"
            outlined
            label="Password"
            prepend-inner-icon="mdi-lock"
            :error-messages="errors.password"
          ></v-text-field>

          <v-alert v-if="errors.general" type="error" dense class="mb-3">
            {{ errors.general }}
          </v-alert>

          <div class="sns-login__action">
            <v-progress-circular v-if="isLoading" indeterminate color="primary"></v-progress-circular>
            <v-btn v-else color="primary" dark large block @click="login">
              Login
            </v-btn>
          </div>

          <p class="sns-login__help">Trouble signing in? Contact
            <a href="mailto:support@aaibuzz.com">support@aaibuzz.com</a>
          </p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions } from "vuex";

export default {
  name: "LoginPage",
  data() {
    return {
      form: {
        email: "",
        password: "",
        portal: true,
      },
      loggedInRole: "",
      isLoading: false,
      errors: {},
    };
  },
  methods: {
    ...mapActions({
      logIn: "auth/logIn",
    }),
    async login() {
      this.isLoading = true;
      this.errors = {}; // Clear previous errors

      try {
        // Perform login
        await this.logIn(this.form);

        // Wait a moment for auth state to be properly set
        await this.$nextTick();

        // Get user role after login to determine redirect
        const user = this.$store.getters["auth/user"];
        const isAuthenticated = this.$store.getters["auth/authenticated"];

        if (isAuthenticated && user) {
          // Determine redirect based on user role
          let redirectRoute = '/home'; // Default route

          if (user.roles && user.roles.length > 0) {
            const roleName = user.roles[0].name;
            if (roleName === 'CANDIDATE' || roleName === 'COMPANY') {
              redirectRoute = '/profile';
            }
          }

          console.log(`Login successful, redirecting to: ${redirectRoute}`);

          // Navigate to appropriate route
          await this.$router.replace(redirectRoute);
        } else {
          throw new Error('Authentication failed - user data not loaded');
        }

      } catch (error) {
        console.error('Login error:', error);

        // Show user-friendly error message
        if (error.response && error.response.status === 401) {
          this.errors = { email: 'Invalid email or password' };
        } else {
          this.errors = { general: 'Login failed. Please try again.' };
        }
      } finally {
        this.isLoading = false;
      }
    },
  },
};
</script>

<style scoped>
.sns-login {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  box-sizing: border-box;
}

.sns-login__card {
  width: 100%;
  max-width: 960px;
  display: grid;
  grid-template-columns: 1.05fr 1fr;
  background: #ffffff;
  border-radius: 18px;
  overflow: hidden;
  box-shadow: 0 24px 60px rgba(8, 38, 60, 0.22);
}

/* ---- hero ---- */
.sns-login__hero {
  position: relative;
  overflow: hidden;
  color: #eaf1f5;
  padding: 48px 44px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  background: linear-gradient(155deg, #08263c 0%, #0c3a5b 52%, #0e5f73 100%);
}

.sns-login__compass {
  position: absolute;
  top: -40px;
  right: -50px;
  width: 260px;
  height: 260px;
  color: #ffffff;
  opacity: 0.10;
  animation: snsSpin 90s linear infinite;
}
@keyframes snsSpin { to { transform: rotate(360deg); } }

.sns-login__hero-content { position: relative; z-index: 1; }

.sns-login__logo-badge {
  width: 84px;
  height: 84px;
  border-radius: 16px;
  background: #ffffff;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 8px;
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.25);
  margin-bottom: 22px;
}
.sns-login__logo-badge img { width: 100%; height: 100%; object-fit: contain; }

.sns-login__brand {
  font-family: 'Bahnschrift', 'Segoe UI', system-ui, sans-serif;
  font-size: 1.6rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  line-height: 1.15;
  margin: 0 0 6px;
}
.sns-login__tagline {
  font-size: 0.9rem;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: #E9C57F; /* brass */
  margin: 0 0 28px;
}

.sns-login__points { list-style: none; padding: 0; margin: 0; }
.sns-login__points li {
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: 0.92rem;
  color: rgba(234, 241, 245, 0.92);
  margin-bottom: 14px;
}

.sns-login__waves {
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  width: 100%;
  height: 110px;
  display: block;
}

/* ---- form ---- */
.sns-login__form {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 48px 44px;
  background: #ffffff;
}
.sns-login__form-inner { width: 100%; max-width: 340px; }

.sns-login__mini-logo {
  display: none;
  width: 76px;
  height: 76px;
  object-fit: contain;
  margin: 0 auto 16px;
}

.sns-login__title {
  font-family: 'Bahnschrift', 'Segoe UI', system-ui, sans-serif;
  font-size: 2rem;
  font-weight: 700;
  color: #0c3a5b;
  margin: 0 0 4px;
}
.sns-login__subtitle {
  font-size: 0.92rem;
  color: #61798a;
  margin: 0 0 26px;
}

.sns-login__action { margin-top: 4px; text-align: center; }
.sns-login__action .v-btn { letter-spacing: 0.08em; }

.sns-login__help {
  margin-top: 20px;
  font-size: 0.8rem;
  color: #8397a5;
  text-align: center;
}
.sns-login__help a { color: #0e8390; text-decoration: none; font-weight: 600; }
.sns-login__help a:hover { text-decoration: underline; }

/* ---- responsive: below 860px, hide hero, show a centered form + mini logo ---- */
@media (max-width: 860px) {
  .sns-login__card { grid-template-columns: 1fr; max-width: 420px; }
  .sns-login__hero { display: none; }
  .sns-login__mini-logo { display: block; }
  .sns-login__form { padding: 40px 28px; }
}

@media (prefers-reduced-motion: reduce) {
  .sns-login__compass { animation: none; }
}
</style>
